// Core
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Fade from 'react-reveal/Fade';

// Styles
import './styles.scss';

// Assets
import { CloseOutlined } from '@ant-design/icons';

import { restAPIs } from 'utils/restAPIs';
import { fetchData } from 'utils/fetch';


import { Select, Spin } from 'antd';
import Movie from 'components/commonComponents/Movie';
import Search from 'components/commonComponents/Search';
import Loader from 'components/commonComponents/Loader';
import EmptyState from 'components/commonComponents/EmptyState';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 2 - 1900 + 1 }, (_, i) => {
    const year = String(CURRENT_YEAR + 2 - i);
    return { value: year, label: year };
});

const Movies = () => {
    const { currentLang, moviesList } = useSelector((store) => store.commonData);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [isLastPage, setIsLastPage] = useState(false);
    const [isLoadingPage, setLoadingPage] = useState(false);
    const [page, setPage] = useState(1);
    const [searchtext, setSearchText] = useState('')

    const [festivalId, setFestivalId] = useState(searchParams.get('festivalId') || '')
    const [awardId, setAwardId] = useState(searchParams.get('awardId') || '')
    const [artistId, setArtistId] = useState('')
    const [year, setYear] = useState('')
    const [festivalOptions, setFestivalOptions] = useState([])
    const [awardOptions, setAwardOptions] = useState([])
    const [artistOptions, setArtistOptions] = useState([])
    const debounceRef = useRef({});

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchMovies();
        fetchAllAwardTitles();
    }, [currentLang]);

    const buildFilterFormData = (overrides = {}) => {
        const f = { festivalId, awardId, artistId, year, ...overrides };
        const formData = new FormData();
        formData.append('language', currentLang?.id)
        if (f.festivalId) formData.append('festivalId', f.festivalId)
        if (f.awardId) formData.append('awardId', f.awardId)
        if (f.artistId) formData.append('artistId', f.artistId)
        if (f.year) formData.append('year', f.year)
        if (searchtext) formData.append('searchtext', btoa(unescape(encodeURIComponent(searchtext))))
        return formData;
    }

    const fetchMovies = async (overrides = {}) => {
        setLoading(true)
        const res = await fetchData(restAPIs.moviesList(buildFilterFormData(overrides)));
        setIsLastPage(res.isLastPage)
        const currentFestivalId = overrides.festivalId ?? festivalId;
        if (currentFestivalId && res.festivalName)
            setFestivalOptions((prev) => prev.some((o) => o.value === currentFestivalId) ? prev : [{ value: currentFestivalId, label: res.festivalName }, ...prev]);
        dispatch({
            type: 'moviesList',
            value: res.items
        });
        setLoading(false)
        setPage(1)
    }

    // Award categories (Best Film, Best Actor, ...) are global, not scoped to
    // a festival — movies.php's awardId filter works on its own. The list is
    // small (~43) and static, so load every page once instead of remote-searching.
    const fetchAllAwardTitles = async () => {
        let all = [];
        let page = 1;
        while (true) {
            const res = await fetchData(restAPIs.awardTitlesList(currentLang?.id, page));
            all = all.concat(res?.entities || []);
            if (res?.isLastPage || !res?.entities?.length) break;
            page += 1;
        }
        setAwardOptions(all.map((a) => ({ value: String(a.awardId), label: a.name })));
    }

    const handleOnClickMovie = (item) => {
        navigate('/movie/' + item.movieId)
    }

    const handlePageClick = () => {
        let curren = page
        setPage(curren + 1);
        fetchMoviesPaginate(curren + 1)
    }
    const fetchMoviesPaginate = async (page) => {
        setLoadingPage(true)
        const formData = buildFilterFormData();
        formData.append('page', page)
        const res = await fetchData(restAPIs.moviesList(formData));
        setIsLastPage(res.isLastPage)
        dispatch({
            type: 'moviesList',
            value: moviesList.concat(res.items)
        });
        setLoadingPage(false)
    }

    const handleSubmit = () => {
        fetchMovies();
    }

    const handleOnChangeSearch = (value) => {
        setSearchText(value)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    };

    const debouncedSearch = (key, run, delay = 350) => {
        clearTimeout(debounceRef.current[key]);
        debounceRef.current[key] = setTimeout(run, delay);
    };

    const searchOptions = async (text, endpoint, idKey) => {
        const formData = new FormData();
        formData.append('language', currentLang?.id);
        formData.append('searchtext', btoa(unescape(encodeURIComponent(text))));
        const res = await fetchData(endpoint(formData));
        return (res.items || []).map((item) => ({ value: String(item[idKey]), label: item.title }));
    };

    const handleFestivalSearch = (text) => {
        if (!text) return setFestivalOptions([]);
        debouncedSearch('festival', async () => setFestivalOptions(await searchOptions(text, restAPIs.festivalsList, 'festivalId')));
    };

    const handleArtistSearch = (text) => {
        if (!text) return setArtistOptions([]);
        debouncedSearch('artist', async () => setArtistOptions(await searchOptions(text, restAPIs.artistList, 'artistId')));
    };

    const handleFestivalChange = (value) => {
        setFestivalId(value || '');
        fetchMovies({ festivalId: value || '' });
    };

    const handleAwardChange = (value) => {
        setAwardId(value || '');
        fetchMovies({ awardId: value || '' });
    };

    const handleArtistChange = (value) => {
        setArtistId(value || '');
        fetchMovies({ artistId: value || '' });
    };

    const handleYearChange = (value) => {
        setYear(value || '');
        fetchMovies({ year: value || '' });
    };

    const hasActiveFilters = festivalId || awardId || artistId || year;

    const handleClearFilters = () => {
        setFestivalId(''); setAwardId(''); setArtistId(''); setYear('');
        setFestivalOptions([]); setArtistOptions([]);
        navigate('/movies');
        fetchMovies({ festivalId: '', awardId: '', artistId: '', year: '' });
    };


    return (
        <>
            <div className='container __innerTopPad'>
                <div className='__movies_toolbar' dir={currentLang?.id === 1 ? 'rtl' : 'ltr'}>
                    <h3 className='__common-head'>{t('movies')}</h3>
                    <Search
                        handleOnClick={handleSubmit}
                        value={searchtext}
                        onChange={handleOnChangeSearch}
                        onKeyDown={handleKeyDown}
                        placeholder={t('search')}
                    />
                </div>

                <div className='__movies_filters' dir={currentLang?.id === 1 ? 'rtl' : 'ltr'}>
                    <Select
                        allowClear
                        placeholder={t('year')}
                        value={year || undefined}
                        onChange={handleYearChange}
                        options={YEAR_OPTIONS}
                        showSearch
                        className='__filter_select'
                    />
                    <Select
                        allowClear
                        placeholder={t('festivals')}
                        value={festivalId || undefined}
                        onChange={handleFestivalChange}
                        onSearch={handleFestivalSearch}
                        options={festivalOptions}
                        showSearch
                        filterOption={false}
                        notFoundContent={null}
                        className='__filter_select'
                    />
                    <Select
                        allowClear
                        placeholder={t('award')}
                        value={awardId || undefined}
                        onChange={handleAwardChange}
                        options={awardOptions}
                        showSearch
                        optionFilterProp='label'
                        className='__filter_select'
                    />
                    <Select
                        allowClear
                        placeholder={t('artists')}
                        value={artistId || undefined}
                        onChange={handleArtistChange}
                        onSearch={handleArtistSearch}
                        options={artistOptions}
                        showSearch
                        filterOption={false}
                        notFoundContent={null}
                        className='__filter_select'
                    />
                    {hasActiveFilters &&
                        <button type='button' className='__filter_chip' onClick={handleClearFilters}>
                            <span>{t('clear_filter')}</span>
                            <CloseOutlined />
                        </button>
                    }
                </div>
                <div className='row'>
                    <div className='col-md-12 text-center'>
                        {isLoading ?
                            <Loader />
                            :
                            <>
                                <div className='__common_grid_list'>
                                    {
                                        moviesList && moviesList?.map((item, idx) => {
                                            return (
                                                <Fade bottom cascade duration={2000}>
                                                    <Movie key={idx}
                                                        image={item.picture}
                                                        title={item.title}
                                                        premium={item.inappFree}
                                                        handleOnClick={() => handleOnClickMovie(item)} 
                                                        year={item.year}
                                                        description={item.description}
                                                        />
                                                </Fade >
                                            )
                                        })}
                                </div>
                                {!isLoading && moviesList?.length === 0 && <EmptyState message={t('no_results_found')} />}
                                {isLoadingPage ? <Spin className='__spin_color' size="large" tip="Loading" /> : ''}</>
                        }
                    </div>
                    {isLoading ? '' :
                        <div className='__pagination' style={{ justifyContent: 'center' }}>
                            {isLastPage ? '' : <button onClick={handlePageClick}>{t('more')} </button>}
                        </div>
                    }
                </div>
            </div>
        </>
    );
};


export default Movies;
